<template>
  <div
    class="setup-task-detail-dependencies"
    data-testid="setup-task-detail-dependencies"
  >
    <div
      v-if="!tableRows.length"
      class="setup-task-detail-dependencies__empty-card"
      data-testid="setup-task-detail-dependencies__empty"
    >
      <div class="setup-task-detail-dependencies__empty-header">
        <h2 class="setup-task-detail-dependencies__empty-heading">Dependencies</h2>
      </div>
      <div class="setup-task-detail-dependencies__empty">
        <div class="setup-task-detail-dependencies__empty-icon">
          <img
            src="/img/icons/fbc-icons/DependenciesEmptyLink.svg"
            alt=""
          />
        </div>
        <p class="setup-task-detail-dependencies__empty-title">
          {{ copy.DETAIL_EMPTY_TITLE }}
        </p>
        <p class="setup-task-detail-dependencies__empty-body">
          {{ copy.DETAIL_EMPTY_BODY }}
        </p>
      </div>
    </div>

    <lb-datatableaction
      v-else
      :headers="tableHeaders"
      :tabledata="tableRows"
      :displayed-row-count="tableRows.length"
      :itemsperpage="tableRows.length || 25"
      :bulk-actions="false"
      :show-toolbar="false"
      :enable-column-reorder="false"
      :enable-cell-truncate="false"
      :page_details="{}"
      :selectfilter="{}"
      header-bg-class="fbc-table-header-bg"
      data-testid="setup-task-detail-dependencies__table"
    >
      <template #tbody="{ headers }">
        <tr
          v-for="(row, index) in tableRows"
          :key="`${row.linkType}-${templateIdString(row._id)}-${index}`"
          class="setup-task-detail-dependencies__row"
          :data-testid="`setup-task-detail-dependencies__row--${index}`"
        >
          <td
            v-for="header in visibleHeaders(headers)"
            :key="`${header.value}-${index}`"
            class="px-4"
            :class="cellClass(header.value)"
            :data-testid="`setup-task-detail-dependencies__cell--${header.value}-${index}`"
          >
            <span
              v-if="header.value === 'linkType'"
              class="setup-task-detail-dependencies__chip"
              :class="`setup-task-detail-dependencies__chip--${row.chipVariant}`"
            >
              {{ row.chipLabel }}
            </span>
            <span v-else-if="header.value === 'ref'">{{ row.ref || '—' }}</span>
            <v-tooltip
              v-else-if="header.value === 'description'"
              bottom
              fixed
              open-delay="300"
              content-class="tooltip-bottom tooltip-wrap"
              class="setup-task-detail-dependencies__name-tooltip"
              :disabled="descriptionLabel(row.description) === (String(row.description || '').trim() || 'Task')"
            >
              <template #activator="{ on, attrs }">
                <span
                  class="setup-task-detail-dependencies__task-name"
                  :class="{ 'setup-task-detail-dependencies__task-name--link': row.canVisit }"
                  role="link"
                  tabindex="0"
                  v-bind="attrs"
                  v-on="on"
                  @click="onNameClick(row)"
                  @keydown.enter="onNameClick(row)"
                >
                  {{ descriptionLabel(row.description) }}
                </span>
              </template>
              <span>{{ row.description }}</span>
            </v-tooltip>
            <span v-else-if="header.value === 'due'">{{ formatDue(row.duedays) }}</span>
            <span v-else-if="header.value === 'location'">{{ row.locationName || '—' }}</span>
          </td>
        </tr>

        <tr v-if="!tableRows.length">
          <td
            :colspan="visibleHeaders(headers).length"
            class="px-4 py-6 text-center setup-task-detail-dependencies__empty"
            data-testid="setup-task-detail-dependencies__empty"
          >
            {{ copy.DETAIL_EMPTY }}
          </td>
        </tr>
      </template>
    </lb-datatableaction>
  </div>
</template>

<script>
import './TaskDetailDependencies.scss';
import { SETUP_TEMPLATE_DEPENDENCY } from '../../../../../utils/constants';
import {
  detailRowsFromDependencyArrays,
  formatTemplateDueDaysLabel,
  templateIdString,
  truncateDependencyDescription,
} from '../../../TaskList/utils/setupDependencyUtils';

export default {
  name: 'TaskDetailDependencies',

  props: {
    blocking: { type: Array, default: () => [] },
    blockedBy: { type: Array, default: () => [] },
    relates: { type: Array, default: () => [] },
  },

  data() {
    return {
      copy: SETUP_TEMPLATE_DEPENDENCY,
    };
  },

  computed: {
    locationHeader() {
      return this.$store.state.LocationHeader || this.copy.COL_LOCATION;
    },

    tableHeaders() {
      return [
        {
          text: this.copy.DETAIL_COL_LINK_TYPE,
          value: 'linkType',
          visible: true,
          sortable: false,
          filterable: true,
          filterLabelMap: {
            [this.copy.LINK_TYPE_BLOCKS]: this.copy.DETAIL_BLOCKING,
            [this.copy.LINK_TYPE_BLOCKING]: this.copy.DETAIL_WAITING_ON,
            [this.copy.LINK_TYPE_RELATED]: this.copy.DETAIL_RELATED,
          },
          headerClass: 'setup-task-detail-dependencies__col-link-type',
        },
        {
          text: this.copy.DETAIL_COL_REF,
          value: 'ref',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'setup-task-detail-dependencies__col-ref',
        },
        {
          text: this.copy.DETAIL_COL_TASK_NAME,
          value: 'description',
          visible: true,
          sortable: false,
          filterable: true,
          headerClass: 'setup-task-detail-dependencies__col-task-name',
        },
        {
          text: this.copy.DETAIL_COL_DUE_DAYS,
          value: 'due',
          visible: true,
          sortable: false,
          filterable: true,
          headerClass: 'setup-task-detail-dependencies__col-due',
        },
        {
          text: this.locationHeader,
          value: 'location',
          visible: true,
          sortable: false,
          filterable: true,
          headerClass: 'setup-task-detail-dependencies__col-location',
        },
      ];
    },

    tableRows() {
      return detailRowsFromDependencyArrays({
        blocking: this.blocking,
        blockedBy: this.blockedBy,
        relates: this.relates,
      });
    },
  },

  methods: {
    templateIdString,

    visibleHeaders(headers) {
      return (headers || this.tableHeaders).filter((header) => header.visible !== false);
    },

    cellClass(value) {
      return {
        'setup-task-detail-dependencies__cell-link-type': value === 'linkType',
        'setup-task-detail-dependencies__cell-ref': value === 'ref',
        'setup-task-detail-dependencies__cell-task-name': value === 'description',
        'setup-task-detail-dependencies__cell-due': value === 'due',
        'setup-task-detail-dependencies__cell-location': value === 'location',
      };
    },

    formatDue(duedays) {
      return formatTemplateDueDaysLabel(duedays);
    },

    descriptionLabel(value) {
      return truncateDependencyDescription(value) || 'Task';
    },

    onNameClick(row) {
      if (!row?.canVisit) return;
      const id = templateIdString(row._id);
      if (!id) return;
      const query = { ...(this.$route.query || {}) };
      const cid = query.cid || this.$store.state.client;
      if (cid) query.cid = String(cid);
      this.$router
        .push({ name: 'fc-setup-task-detail', params: { id }, query })
        .catch((err) => {
          if (err?.name !== 'NavigationDuplicated') {
            console.error('[TaskDetailDependencies]:', err);
          }
        });
    },
  },
};
</script>
